import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X as XIcon, UploadSimple, FilePdf } from "@phosphor-icons/react";

/**
 * ScanUploadModal — upload a scanned PDF of a paper-signed document.
 * Operator declares which signatures are present in the scan.
 */
export default function ScanUploadModal({ open, onClose, movementId, movementType, onUploaded }) {
  const [file, setFile] = useState(null);
  const [hasIncaricato, setHasIncaricato] = useState(true);
  const [hasDestinatario, setHasDestinatario] = useState(true);
  const [uploading, setUploading] = useState(false);
  const isExit = movementType === "exit";

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file PDF"); return; }
    if (file.type !== "application/pdf") { toast.error("Il file deve essere un PDF"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("PDF troppo grande (max 5MB)"); return; }
    if (!hasIncaricato && !hasDestinatario) {
      toast.error("Seleziona almeno una firma presente nel documento");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        try {
          await api.post(`/movements/${movementId}/scan-upload`, {
            pdf_base64: dataUrl,
            has_incaricato_signature: isExit && hasIncaricato,
            has_destinatario_signature: hasDestinatario,
          });
          toast.success("Scansione firmata caricata");
          onUploaded && onUploaded();
          onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
      };
      reader.onerror = () => { setUploading(false); toast.error("Errore lettura file"); };
      reader.readAsDataURL(file);
    } catch (e) { toast.error(formatApiError(e)); setUploading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-2 md:p-4" data-testid="scan-upload-modal">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FilePdf size={20} weight="bold" className="text-[#002FA7]" />
            <h3 className="font-display font-bold text-lg tracking-tight">Carica scansione firmata</h3>
          </div>
          <button onClick={onClose} data-testid="scan-upload-close" className="p-1.5 hover:bg-neutral-100 transition-colors">
            <XIcon size={18} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-xs text-neutral-600">
            Carica la scansione PDF del documento firmato a mano (es. dopo consegna al destinatario, o per firma cartacea in caso di problemi tecnici).
          </p>

          <div>
            <label className="label-eyebrow block mb-1">File PDF firmato</label>
            <input
              data-testid="scan-upload-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            {file && <div className="text-xs text-neutral-500 mt-1">{file.name} ({(file.size/1024).toFixed(0)} KB)</div>}
          </div>

          <div>
            <div className="label-eyebrow mb-2">Firme presenti nel documento</div>
            <div className="space-y-2">
              {isExit && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="scan-has-incaricato"
                    type="checkbox"
                    checked={hasIncaricato}
                    onChange={(e) => setHasIncaricato(e.target.checked)}
                  />
                  <span className="text-sm">Firma <b>Incaricato del trasporto</b></span>
                </label>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="scan-has-destinatario"
                  type="checkbox"
                  checked={hasDestinatario}
                  onChange={(e) => setHasDestinatario(e.target.checked)}
                />
                <span className="text-sm">Firma <b>Destinatario</b></span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <button onClick={onClose} className="px-3 py-2 text-sm border border-neutral-300 hover:bg-neutral-50">Annulla</button>
            <button
              data-testid="scan-upload-submit"
              onClick={submit}
              disabled={uploading || !file}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] disabled:opacity-40 text-white transition-colors"
            >
              <UploadSimple size={16} weight="bold" />
              {uploading ? "Caricamento..." : "Carica"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
